import { isAndroidWebView, isIosWebView } from "./platform";
import { openAppSettings } from "../native/androidBridge";
import { openIosAppSettings, iosBridgeAvailable } from "../native/iosBridge";

// Recovery path for camera / mic / geolocation when the OS permission is denied
// or blocked inside the native app (Android + iOS). The web APIs now correctly
// reject (native no longer grants the WebView request unconditionally), so
// callers catch the rejection and offer to open the app's system Settings page.
//
// There's no bridge state-query for these (notifications-only), so we detect
// denial from the standard Web API errors and offer Settings on demand.

/**
 * True inside a native wrapper (Android or iOS) that can open its own system
 * Settings page. iOS is detected by the UA marker OR a live bridge handle, since
 * the UA marker may not be set yet (mirrors useNativeBridge /
 * useNotificationPermission).
 */
export function canOpenNativeAppSettings(): boolean {
  return isAndroidWebView() || isIosWebView() || iosBridgeAvailable();
}

/**
 * Opens the app's system Settings page on whichever native wrapper we're in.
 * No-op on a normal browser (browser permission UI handles recovery there).
 */
export function openNativeAppSettings(): void {
  if (isAndroidWebView()) {
    openAppSettings();
  } else if (isIosWebView() || iosBridgeAvailable()) {
    openIosAppSettings();
  }
}

/**
 * In the native app, asks the user (native confirm dialog) whether to open the
 * app's system Settings to enable the permission, and opens it if confirmed.
 * No-op on a normal browser (browser permission UI handles recovery there).
 */
export function offerOpenSettings(message: string): void {
  if (!canOpenNativeAppSettings()) return;
  // window.confirm renders a native dialog inside the WebView.
  if (window.confirm(`${message}\n\nOpen app settings to enable?`)) {
    openNativeAppSettings();
  }
}
